import { Button, Dropdown, SelectDropdownOption } from '@/components';
import { AttachUserToEntityModal } from '@/components/AttachUserToEntityModal';
import { useInvalidateBusinessAreaQueries } from '@/modules/BusinessAreas/hooks/use-invalidate-business-areas-queries';
import { useGetAllBusinessAreasByCompany } from '@/modules/BusinessAreas/queries';
import { useAttachUserBusinessArea } from '@/modules/BusinessAreas/queries/attach-user-businessArea';
import { useGetAllCompaniesByOrganization } from '@/modules/Companies/queries';
import { useGetAllOrganizations } from '@/modules/Organizations/queries';
import { useGetBusinessAreaUserProfiles } from '@/modules/Users/queries/get-business-area-user-profiles';
import { getDropdownArray } from '@/utils/get-dropdown-array.util';
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

interface AttachUserToBusinessModalProps {
  userId: number;
}

export const AttachUserToBusinessModal = ({
  userId,
}: AttachUserToBusinessModalProps): ReactElement => {
  const { t } = useTranslation();
  const [dropdownOrganizationValue, setOrganizationDropdownEntityValue] =
    useState<number>();
  const [dropdownCompanyValue, setDropdownCompanyValue] = useState<number>();
  const [dropdownBusinessValue, setDropdownBusinessValue] = useState<number>();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const invalidateBusinessAreaQueries = useInvalidateBusinessAreaQueries();
  const { data: roles } = useGetBusinessAreaUserProfiles();

  const handleModalState = () => setIsOpen(!isOpen);

  const { mutate, isLoading: isSubmitting } = useAttachUserBusinessArea();

  const handleConfirm = (roleId: number) => {
    if (!dropdownBusinessValue) return;
    mutate(
      {
        businessAreaId: dropdownBusinessValue,
        userProfiles: [
          {
            userId,
            roleId,
          },
        ],
      },
      {
        onError: () => {
          toast.error(
            t('error_attaching_user_to_entity', { entity: t('businessArea') }),
          );
        },
        onSuccess: () => {
          invalidateBusinessAreaQueries();
          handleModalState();
          toast.success(
            t('success_attaching_user_to_entity', {
              entity: t('businessArea'),
            }),
          );
        },
      },
    );
  };

  const { data: organizationData } = useGetAllOrganizations();

  const { data: companiesData } = useGetAllCompaniesByOrganization(
    dropdownOrganizationValue,
  );

  const { data: businessData } =
    useGetAllBusinessAreasByCompany(dropdownCompanyValue);

  const organizationDropdownOptions = useMemo(
    () => getDropdownArray(organizationData ?? []),
    [organizationData],
  );

  const companiesDropdownOptions = useMemo(
    () => getDropdownArray(companiesData ?? []),
    [companiesData],
  );

  const businessDropdownOptions = useMemo(
    () => getDropdownArray(businessData ?? []),
    [businessData],
  );

  const handleOnSelectOrganizationDropdownValue = useCallback(
    (option: SelectDropdownOption<number>) =>
      setOrganizationDropdownEntityValue(option.value),
    [],
  );

  const handleOnSelectCompanyDropdownValue = useCallback(
    (option: SelectDropdownOption<number>) =>
      setDropdownCompanyValue(option.value),
    [],
  );

  const handleOnSelectBusinessDropdownValue = useCallback(
    (option: SelectDropdownOption<number>) =>
      setDropdownBusinessValue(option.value),
    [],
  );

  useEffect(() => {
    setOrganizationDropdownEntityValue(undefined);
  }, [isOpen]);

  useEffect(() => {
    setDropdownCompanyValue(undefined);
  }, [dropdownOrganizationValue]);

  useEffect(() => {
    setDropdownBusinessValue(undefined);
  }, [dropdownCompanyValue]);

  useEffect(() => {
    if (organizationData?.length == 1) {
      setOrganizationDropdownEntityValue(organizationData[0].id);
    }

    if (companiesData?.length == 1) {
      setDropdownCompanyValue(companiesData[0].id);
    }

    if (businessData?.length == 1) {
      setDropdownBusinessValue(businessData[0].id);
    }
  }, [businessData, companiesData, organizationData]);

  return (
    <>
      <Button onClick={handleModalState} variant="primary">
        {t('attach_entity', { entity: t('businessArea') })}
      </Button>
      <AttachUserToEntityModal
        isOpen={isOpen}
        isSubmitting={isSubmitting}
        entityType={'businessArea'}
        onHandleClose={handleModalState}
        onHandleConfirm={handleConfirm}
        isConfirmDisabled={!dropdownBusinessValue}
        roles={roles ?? []}
        roleEntity={'business-area'}
      >
        <div className="text-sm font-bold capitalize">
          {t('select_existing_entity', { entity: t('organizations') })}
        </div>
        <Dropdown
          className="h-10 w-full rounded-md  bg-brightGray capitalize italic"
          placeholder={t('select')}
          options={organizationDropdownOptions}
          value={dropdownOrganizationValue}
          onSelect={handleOnSelectOrganizationDropdownValue}
        />
        <div className="text-sm font-bold capitalize">
          {t('select_existing_entity', { entity: t('companies') })}
        </div>
        <Dropdown
          className="h-10 w-full rounded-md  bg-brightGray capitalize italic"
          placeholder={t('select')}
          options={companiesDropdownOptions}
          value={dropdownCompanyValue}
          onSelect={handleOnSelectCompanyDropdownValue}
        />
        <div className="text-sm font-bold capitalize">
          {t('select_existing_entity', { entity: t('businessArea') })}
        </div>
        <Dropdown
          className="h-10 w-full rounded-md  bg-brightGray capitalize italic"
          placeholder={t('select')}
          options={businessDropdownOptions}
          value={dropdownBusinessValue}
          onSelect={handleOnSelectBusinessDropdownValue}
        />
      </AttachUserToEntityModal>
    </>
  );
};
